import { MonthlyReportRow } from "../../MonthlyReport/api/fetchMonthlyReport";

export type TierFilter = "all" | "1" | "2";

export interface LeaderboardEntry {
  rank: number;
  fullName: string;
  company: string;
  cityState: string;
  tier: string;
  value: number;
  valueDisplay: string;
}

export interface LeaderboardCardData {
  category: string;
  valueLabel: string;
  primaryMetric: string;
  totalMembers: number;
  entries: LeaderboardEntry[];
}

export interface TradeLeaderboard {
  trade: string;
  cards: LeaderboardCardData[];
}

const TOP_N = 10;

const toNumber = (value: string | number): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Canonicalizes a row's tier for comparison against the `"1"`/`"2"` filter
 * values. The contract is a bare `"1"`/`"2"` string, but the backend could hand
 * back a number, a padded string, or `"1.0"`; normalizing (trim + integer-cast
 * when numeric) keeps the filter from silently matching nothing on a benign
 * representation difference.
 */
export const normalizeTier = (
  value: string | number | null | undefined,
): string => {
  if (value == null) {
    return "";
  }
  const trimmed = String(value).trim();
  // Guard before the numeric cast: `Number("")` and `Number(" ")` are `0`
  // (an integer), which would otherwise turn a blank/whitespace tier into the
  // spurious tier `"0"`. An absent tier must stay empty so it matches nothing.
  if (trimmed === "") {
    return "";
  }
  const numeric = Number(trimmed);
  return Number.isInteger(numeric) ? String(numeric) : trimmed;
};

/**
 * Formats a metric value for display, inferring the unit from its label.
 * `%` categories store a 0–1 fraction (e.g. 51/51 = 1) → shown as a percentage.
 * `Revenue` categories are large dollar figures. Everything else is a count.
 */
export const formatMetricValue = (value: number, label: string): string => {
  // `label` is typed `string` but nullable in practice (the API can hand back
  // a null metric label); coalesce so the `.includes`/regex checks can't throw.
  const safeLabel = label ?? "";
  if (safeLabel.includes("%")) {
    return `${(value * 100).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    })}%`;
  }
  if (/revenue|\$/i.test(safeLabel)) {
    return `$${Math.round(value).toLocaleString()}`;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/**
 * The value a category ranks its members on. Mirrors the report's ORDER BY:
 * `numerator`-primary categories rank on the numerator, the rest on calculated.
 */
const metricForRow = (row: MonthlyReportRow): number =>
  row.primary_metric === "numerator"
    ? toNumber(row.numerator)
    : toNumber(row.calculated);

const labelForRow = (row: MonthlyReportRow): string =>
  row.primary_metric === "numerator"
    ? row.numerator_label
    : row.calculated_label;

/**
 * Groups the flat monthly-report rows into per-trade leaderboards: one card per
 * KPI stat (category), each holding the top 10 members ranked by that category's
 * primary metric, descending. Optionally narrowed to a single company tier.
 */
export const buildLeaderboards = (
  rows: MonthlyReportRow[],
  tier: TierFilter,
): TradeLeaderboard[] => {
  const filtered =
    tier === "all"
      ? rows
      : rows.filter((row) => normalizeTier(row.tier) === tier);

  const byTrade = new Map<string, Map<string, MonthlyReportRow[]>>();
  for (const row of filtered) {
    // `trade` is typed `string` but nullable in practice. Coalesce to "" at the
    // grouping key so the value that later flows into the Radix Tabs
    // `value`/`TabsTrigger`/`TabsContent` props is never null/undefined — a null
    // there renders an empty tab strip with no selectable cards.
    const tradeKey = row.trade ?? "";
    let byCategory = byTrade.get(tradeKey);
    if (!byCategory) {
      byCategory = new Map();
      byTrade.set(tradeKey, byCategory);
    }
    const list = byCategory.get(row.category);
    if (list) {
      list.push(row);
    } else {
      byCategory.set(row.category, [row]);
    }
  }

  const trades: TradeLeaderboard[] = [];
  for (const [trade, byCategory] of byTrade) {
    const cards: LeaderboardCardData[] = [];
    for (const [category, categoryRows] of byCategory) {
      const valueLabel = labelForRow(categoryRows[0]);
      const ranked = [...categoryRows].sort(
        (a, b) => metricForRow(b) - metricForRow(a),
      );

      const entries: LeaderboardEntry[] = ranked
        .slice(0, TOP_N)
        .map((row, index) => {
          const value = metricForRow(row);
          return {
            rank: index + 1,
            fullName: row.full_name,
            company: row.company,
            cityState: row.city_state ?? "",
            tier: normalizeTier(row.tier),
            value,
            valueDisplay: formatMetricValue(value, valueLabel),
          };
        });

      cards.push({
        category,
        valueLabel,
        primaryMetric: categoryRows[0].primary_metric,
        totalMembers: categoryRows.length,
        entries,
      });
    }
    cards.sort((a, b) => a.category.localeCompare(b.category));
    trades.push({ trade, cards });
  }

  // `trade` is typed `string` but nullable in practice; coalesce both sides so
  // a null trade name can't throw inside the sort comparator.
  trades.sort((a, b) => (a.trade ?? "").localeCompare(b.trade ?? ""));
  return trades;
};
